import { z } from 'zod';

import { BookingHistorySchema, BookingMode, BookingStatus, RefundHistorySchema } from './booking';
import { CheckInHistorySchema } from './check-in-history';
import { CustomQuestionAnswerBookingResponseSchema } from './custom-question-answer';
import { BaseParticipantSchema } from './participants';
import { BookingPriceTierSchema } from './price-tiers';
import { EmailStringSchema } from '../../common';
import { ExperienceSettingsSchema } from '../experience/experience-settings';
import { PaymentMethods } from '../payment-method/public';

export const BookingWithParticipantSchema = z.object({
  bookingId: z.string().uuid(),
  status: z.nativeEnum(BookingStatus),
  createdAt: z.string().datetime(),
  isModified: z.boolean(),
  experienceId: z.string().uuid(),
  startDateTime: z.string(),
  sessionDuration: z.number(),
  confirmationCode: z.string(),
  chargeId: z.string(),
  isCancelled: z.boolean(),
  isPartiallyCancelled: z.boolean(),
  mode: z.nativeEnum(BookingMode),
  eventId: z.string().uuid(),
  purchaser: z.object({
    id: z.string(),
    firstName: z.string(),
    lastName: z.string(),
    emailAddress: EmailStringSchema.nullable(),
    phoneNumber: z.string().optional().nullable(),
    customQuestionAnswers: z.array(CustomQuestionAnswerBookingResponseSchema).optional(),
  }),
  notes: z.string().nullable(),
  amount: z.number(),
  currency: z.string(),
  experienceDiscountAmount: z.number(),
  experiencePaidAmount: z.number(),
  participants: z.array(
    BaseParticipantSchema.extend({
      isCheckedIn: z.boolean(),
      ticketNumber: z.string().nullable(),
      checkInHistories: z.array(CheckInHistorySchema),
      customQuestionAnswers: z.array(CustomQuestionAnswerBookingResponseSchema).optional(),
    }),
  ),
  paymentMethod: z.nativeEnum(PaymentMethods),
  isVoided: z.boolean(),
  isFullyRefunded: z.boolean(),
  isPartiallyRefunded: z.boolean(),
  refundAmount: z.number().optional(),
  refundHistory: z.array(RefundHistorySchema).nullable(),
  experienceSettings: ExperienceSettingsSchema,
  internalNotes: z.string().nullable(),
  brand: z.object({
    id: z.string(),
    name: z.string(),
  }),
  isDisputed: z.boolean(),
  disputedReason: z.string().nullable(),
  priceTiers: z.array(BookingPriceTierSchema),
  cardBrand: z.string().nullable().optional(),
  cardLast4: z.string().nullable().optional(),
  resourceGroupCollectionId: z.string().nullable(),
  bookingHistories: z.array(BookingHistorySchema).nullable(),
});

export type BookingWithParticipant = z.infer<typeof BookingWithParticipantSchema>;
